import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSessionQuery } from "./queries/useSessionQuery";
import { useUserRolesQuery } from "./queries/useUserRolesQuery";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@/lib/logger";
import { useLocation } from "react-router-dom";

export type UserRole = "admin" | "supplier" | "buyer" | "support" | null;

const ACTIVE_ROLE_KEY = "holescale_active_role";
// Track which users we've already logged profile warnings for to reduce console noise
const loggedMissingProfiles = new Set<string>();

export function useUserRole() {
  const [role, setRole] = useState<UserRole>(null);
  const queryClient = useQueryClient();
  const location = useLocation();

  // Use React Query for session (cached, deduplicated)
  const { data: session } = useSessionQuery();
  const userId = session?.user?.id || null;
  const sessionEmail = session?.user?.email || '';
  const sessionFullName = (session?.user?.user_metadata as { full_name?: string } | undefined)?.full_name || null;

  // Use React Query for user roles (cached, deduplicated)
  const { data: rolesData, isLoading, isFetching, refetch } = useUserRolesQuery(userId);

  const allRoles = useMemo(() => rolesData?.roles || [], [rolesData?.roles]);
  // CRITICAL FIX: Also check isFetching to handle cases where queries are refetching
  // after invalidation. When queries are invalidated, isLoading might be false
  // (because there's cached data) but isFetching is true (because a refetch is happening).
  const loading = isLoading || isFetching;

  // NOTE: Auth state listener removed - now handled in AuthContext
  // This prevents duplicate listeners and race conditions

  useEffect(() => {
    if (!userId) {
      setRole(null);
      localStorage.removeItem(ACTIVE_ROLE_KEY);
      return;
    }

    // CRITICAL: Check explicit portal navigation FIRST, synchronously
    // This must happen before any async operations to prevent redirects
    // Even if allRoles is still loading, check the pathname and set role immediately
    const currentPath = location.pathname;
    const explicitPortalRole =
      currentPath.startsWith('/buyer') ? 'buyer' :
        currentPath.startsWith('/supplier') ? 'supplier' :
          currentPath.startsWith('/admin') ? 'admin' :
            null;

    // If user is explicitly on a portal route, set it IMMEDIATELY
    // This prevents any redirect logic from running based on priority
    // We check allRoles to ensure user has that role, but if roles are still loading,
    // we still set the role to prevent redirects during loading
    if (explicitPortalRole) {
      // If roles are loaded and user has the role, set it
      if (allRoles.length > 0 && allRoles.includes(explicitPortalRole)) {
        setRole(explicitPortalRole);
        localStorage.setItem(ACTIVE_ROLE_KEY, explicitPortalRole);
        return; // Exit early - don't run any other role assignment logic
      }
      // If roles are still loading, set the role anyway to prevent redirects
      // This will be corrected once roles load if user doesn't have the role
      if (loading && explicitPortalRole) {
        setRole(explicitPortalRole);
        localStorage.setItem(ACTIVE_ROLE_KEY, explicitPortalRole);
        // Don't return - let the async function run to verify the role
      }
    }

    const loadRolePreference = async () => {
      if (allRoles.length === 0 && !loading) {
        // user has no roles in DB. Do NOT default to buyer.
        // Let them stay on Explore (role=null).
        setRole(null);
        return;
      }

      if (allRoles.length === 0) return;

      // Timeout protection for profile fetch
      const profilePromise = supabase
        .from("profiles")
        .select("preferred_role")
        .eq("id", userId)
        .maybeSingle();

      const timeoutPromise = new Promise<{ data: null }>((resolve) =>
        setTimeout(() => resolve({ data: null }), 3000)
      );

      let profile;
      try {
        const result = await Promise.race([profilePromise, timeoutPromise]);
        profile = result.data;
      } catch (err) {
        logger.error('Profile fetch failed with timeout', { error: err, userId });
        // Continue with default role assignment
        setRole(allRoles.includes("admin") ? "admin" : allRoles[0] || "buyer");
        return;
      }

      if (!profile) {
        // Only log once per user to reduce console noise
        if (!loggedMissingProfiles.has(userId)) {
          logger.debug('Profile not found for user, will create default', { userId });
          loggedMissingProfiles.add(userId);
        }

        // Non-blocking profile creation - only insert if profile doesn't exist
        // CRITICAL: Don't use upsert here as it would overwrite existing data
        // Instead, try to insert and ignore conflicts (profile might have been created by trigger)
        setTimeout(async () => {
          try {
            // First check if profile exists (race condition check)
            const { data: existingProfile } = await supabase
              .from("profiles")
              .select("id")
              .eq("id", userId)
              .maybeSingle();

            // Only insert if profile doesn't exist
            if (!existingProfile) {
              const { error } = await supabase
                .from("profiles")
                .insert({
                  id: userId,
                  email: sessionEmail,
                  full_name: sessionFullName,
                });

              if (error && error.code !== '23505') { // Ignore duplicate key errors
                logger.error('Failed to create profile', { error, userId });
              } else {
                logger.info('Profile created successfully', { userId });
              }
            } else {
              logger.debug('Profile already exists, skipping creation', { userId });
            }
          } catch (err) {
            logger.error('Failed to create profile', { error: err, userId });
          }
        }, 0);

        // Do NOT set default role immediately if profile creation failed/timed out
        // setRole(allRoles.includes("admin") ? "admin" : allRoles[0] || "buyer");
        if (allRoles.length === 0) setRole(null);
        else setRole(allRoles.includes("admin") ? "admin" : allRoles[0]);
        return;
      }

      const preferredRole = profile.preferred_role as UserRole | null;
      const storedRole = localStorage.getItem(ACTIVE_ROLE_KEY) as UserRole;

      // Check if user is explicitly navigating to a specific portal
      // If they are, respect that choice even if it's not the priority role
      // This allows multi-role users to access any portal they have a role for
      const currentPath = location.pathname;
      const explicitPortalRole =
        currentPath.startsWith('/buyer') ? 'buyer' :
          currentPath.startsWith('/supplier') ? 'supplier' :
            currentPath.startsWith('/admin') ? 'admin' :
              null;

      // SECURITY: Only allow roles that exist in database
      // CRITICAL: Admin role always takes priority - admin users should default to admin
      // BUT: Allow admin to act as buyer/supplier for UI testing
      // Priority: Explicit Portal Navigation > Stored Preference (including admin acting roles) > Admin Default > Database Preference > Supplier > Buyer > Support
      if (allRoles.includes("admin")) {
        // Admin users can act as buyer/supplier even without those roles in DB
        if (storedRole && (storedRole === "buyer" || storedRole === "supplier")) {
          // Admin explicitly switched to buyer/supplier - respect that
          setRole(storedRole);
          return; // Exit early to prevent override
        }
        // Admin users default to admin role, but respect stored preference if switching to another valid role
        if (storedRole && allRoles.includes(storedRole) && storedRole !== "admin") {
          // User explicitly switched away from admin - respect that
          setRole(storedRole);
        } else {
          // Default to admin for admin users
          setRole("admin");
          // Sync to localStorage to ensure consistency
          if (!storedRole || storedRole !== "admin") {
            localStorage.setItem(ACTIVE_ROLE_KEY, "admin");
          }
        }
      } else if (explicitPortalRole && allRoles.includes(explicitPortalRole)) {
        // User is explicitly navigating to a portal - respect that choice
        // This allows multi-role users to access any portal they have a role for
        // CRITICAL: This must come before priority checks to allow explicit navigation
        setRole(explicitPortalRole);
        localStorage.setItem(ACTIVE_ROLE_KEY, explicitPortalRole);
      } else if (storedRole && allRoles.includes(storedRole)) {
        // User has a stored preference and it's valid - use it
        setRole(storedRole);
      } else if (preferredRole && allRoles.includes(preferredRole)) {
        // Use database preferred role if valid
        setRole(preferredRole);
        // Sync to localStorage for consistency
        localStorage.setItem(ACTIVE_ROLE_KEY, preferredRole);
      } else if (allRoles.includes("supplier")) {
        setRole("supplier");
        localStorage.setItem(ACTIVE_ROLE_KEY, "supplier");
      } else if (allRoles.includes("buyer")) {
        setRole("buyer");
        localStorage.setItem(ACTIVE_ROLE_KEY, "buyer");
      } else if (allRoles.includes("support")) {
        setRole("support");
        localStorage.setItem(ACTIVE_ROLE_KEY, "support");
      } else {
        // Default to buyer if no roles found
        setRole("buyer");
      }
    };

    loadRolePreference();
  }, [userId, allRoles, loading, location.pathname, sessionEmail, sessionFullName]);

  const switchRole = (newRole: UserRole) => {
    if (!newRole || !allRoles.includes(newRole)) {
      logger.error("Cannot switch to role", { newRole, allRoles });
      return;
    }

    setRole(newRole);
    localStorage.setItem(ACTIVE_ROLE_KEY, newRole);
  };

  return {
    role,
    allRoles,
    loading,
    userId,
    switchRole,
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
      queryClient.invalidateQueries({ queryKey: ['user_roles', userId] });
      refetch();
    }
  };
}
